import { useCallback, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { v4 as uuid } from 'uuid'

import { IconButton } from 'tools/Uniform'
import { stopEvent } from 'utils/event'

import { useOnClickOutside, useOnResizeOrScroll } from 'reducer/global/hooks'

export const CLOSE_BUTTON = {
  NONE: Symbol('no-close-button'),
  INSET: Symbol('inset-close-button'),
  OUTSET: Symbol('outset-close-button')
}

export function Menu({
  showState: [show, setShow],
  label,
  children,
  closeButton = CLOSE_BUTTON.NONE,
  closeOnClick = true,
  disabled = false,
  className = '',
  style = undefined,
  wrapClass = ''
}) {
  const [{ x, y, id }, setState] = useState({
    x: '-1000px',
    y: '-1000px',
    id: uuid()
  })
  const parentRef = useRef(null)
  const innerRef = useRef(null)
  const parents = useMemo(
    () => (closeOnClick ? parentRef : [parentRef, innerRef]),
    [closeOnClick]
  )

  const close = useCallback(() => setShow(false), [setShow])

  const onPageChange = useCallback(() => {
    const parent = parentRef.current
    const child = innerRef.current

    if (!parent || !show) return null

    const { left, right, bottom: y } = parentRef.current.getBoundingClientRect()

    if (child) {
      const { width } = child.getBoundingClientRect()
      const x = left + width > document.body.clientWidth ? right - width : left
      setState((s) => ({ ...s, x, y }))
    } else {
      setState((s) => ({ ...s, x: s.x || left, y }))
    }
  }, [show])

  useOnResizeOrScroll(onPageChange, { type: 'throttle', wait: 100 })
  useOnClickOutside(parents, close)

  return (
    <div className={wrapClass} ref={parentRef}>
      {label && (
        <button
          style={style}
          className={className}
          disabled={disabled}
          onClick={(e) => {
            stopEvent(e)
            setShow(!show)
          }}
          aria-expanded={show}
          aria-haspopup="menu"
        >
          {label}
        </button>
      )}
      {show &&
        createPortal(
          <div
            ref={innerRef}
            className="fixed normal"
            style={{ left: x, top: y }}
          >
            {closeButton !== CLOSE_BUTTON.NONE && (
              <div className="base-body">
                <IconButton
                  type="close"
                  style={
                    closeButton === CLOSE_BUTTON.OUTSET
                      ? { transform: 'translate(50%, -50%)' }
                      : undefined
                  }
                  buttonClass="absolute right-0 neutral small"
                  onClick={(e) => {
                    stopEvent(e)
                    setShow(false)
                  }}
                />
              </div>
            )}
            {children}
          </div>,
          document.body,
          id
        )}
    </div>
  )
}

export function MenuButton({ menu, children, ...args }) {
  const showState = useState(false)
  return (
    <Menu showState={showState} label={children} hasCloseButton={false} {...args}>
      {menu}
    </Menu>
  )
}

export function MenuItem({ children }) {
  return <div className="nowrap">{children}</div>
}

export function MenuList({ children }) {
  return <div className="flex flex-column">{children}</div>
}

export function SubMenuItem({ menu, children, className = '' }) {
  const [elem, setElem] = useState(null)
  const refCallback = useCallback((elem) => setElem(elem), [])
  const style = useMemo(() => {
    const onLeft = { right: '100%' }
    const onRight = { left: '100%' }
    if (!elem) return onLeft

    const { left: distLeft, right } = elem.getBoundingClientRect()
    const distRight = window.innerWidth - right
    return distLeft > distRight ? onLeft : onRight
  }, [elem])

  return (
    <div className={`${className} relative hover-show`}>
      <div
        ref={refCallback}
        className="absolute top-0 hover-show-elem"
        style={style}
      >
        {menu}
      </div>
      <div className="w-100">{children}</div>
    </div>
  )
}
